/**
 * LoraWizard Component
 *
 * Two-mode container:
 *
 * - **Mock mode** (no `loraId`, or a placeholder demo ID): drives the
 *   slides from `useLoraWizardState`'s simulated training loop.
 *   Preserves the original PassportsPage demo behaviour and acts as the
 *   visual fallback while the backend wiring is exercised in real mode.
 *
 * - **Real mode** (`loraId` is a backend UUID): drives the slides from
 *   `useLoraWizardBackend`, which stitches the Phase 06 TanStack-Query
 *   hooks (`useLora`, `useLoraBaseModels`, `useTrainingRun`,
 *   `useSubmitTraining`, etc.) plus the existing `useAssets` upload
 *   path for dataset images. In real mode the `Testing` slide is
 *   skipped — automatic test inference is not in v1 (catalogued for
 *   Phase 09).
 */

import {
  useEffect,
  useCallback,
  useMemo,
  useRef,
  useState,
  useLayoutEffect,
} from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Swiper, SwiperSlide } from "swiper/react";
import type { Swiper as SwiperType } from "swiper";
import { Check, ChevronLeft, ChevronRight } from "lucide-react";
import "swiper/css";

import { Button } from "@/components/ui/button";
import { useLoraWizardState } from "./useLoraWizardState";
import { useLoraWizardBackend } from "./useLoraWizardBackend";
import { SLIDE_CONFIGS, DEFAULT_SETTINGS } from "./types";
import type { TrainingSettings, ReferenceImage } from "./types";
import { IntroModelSlide } from "./slides/IntroModelSlide";
import { ReferencesSlide } from "./slides/ReferencesSlide";
import type { SelectedAsset } from "./components/LibraryAssetDrawer";
import { PrepareSlide } from "./slides/PrepareSlide";
import { SettingsSlide } from "./slides/SettingsSlide";
import { ConfirmSlide } from "./slides/ConfirmSlide";
import { TrainingSlide } from "./slides/TrainingSlide";
import { TestingSlide } from "./slides/TestingSlide";
import { LockSlide } from "./slides/LockSlide";
import { cn } from "@/lib/utils";
import {
  buildSubmitTrainingParams,
  deriveTrainingProgress,
  deriveTrainingSummary,
  isLoraIdReal,
  isTrainingRunActive,
} from "./real-mode";
import {
  useDatasetPrep,
  useStartDatasetPrep,
  useUpdateDatasetCaption,
} from "@/hooks/loras/useDatasetPrep";
import { loraQueryKeys } from "@/hooks/loras/queryKeys";
import {
  useSetDefaultCheckpoint,
  useUpdateLora,
} from "@/hooks/loras/useLoraMutations";
import "./LoraWizard.css";

const ALL_STEPS = [
  { id: 0, label: "Model" },
  { id: 1, label: "References" },
  { id: 2, label: "Prepare" },
  { id: 3, label: "Settings" },
  { id: 4, label: "Confirm" },
  { id: 5, label: "Training" },
  { id: 6, label: "Testing" },
  { id: 7, label: "Lock" },
];

// Prepare (2) is real-mode only; Testing (6) is mock-mode only. Each mode hides
// the other's slide from the indicator and skips it in navigation.
const PREPARE_SLIDE = 2;
const REFERENCES_SLIDE = 1;
const CONFIRM_SLIDE = 4;
const TRAINING_SLIDE = 5;
const TESTING_SLIDE = 6;
const LOCK_SLIDE = 7;

const MOCK_MODE_STEPS = ALL_STEPS.filter((s) => s.id !== PREPARE_SLIDE);
const REAL_MODE_STEPS = ALL_STEPS.filter((s) => s.id !== TESTING_SLIDE);

const MIN_PREP_IMAGES = 5;

interface LoraWizardProps {
  /** Display name shown in headings; used to seed the mock trigger word. */
  castName: string;
  /**
   * Backend LoRA UUID. When supplied AND a valid UUID, the wizard runs
   * in real mode against the Phase 02 API. Placeholder demo IDs (1..4)
   * are silently treated as `undefined` so the mock simulator continues
   * to drive those cards.
   */
  loraId?: string;
  /**
   * Optional project ID for selecting library assets. When provided, the
   * References slide shows a "Browse Library" button that opens a drawer
   * for selecting images from this project to add as training references.
   */
  libraryProjectId?: string;
  onComplete?: () => void;
}

export function LoraWizard({
  castName,
  loraId: loraIdProp,
  libraryProjectId,
  onComplete,
}: LoraWizardProps) {
  const swiperRef = useRef<SwiperType | null>(null);
  const queryClient = useQueryClient();

  const realModeLoraId = isLoraIdReal(loraIdProp) ? loraIdProp : undefined;
  const realMode = Boolean(realModeLoraId);

  const wizardState = useLoraWizardState(castName);

  // Backend hook is only used in real mode; in mock mode we never read
  // any of its outputs (the hook still runs to satisfy hook ordering,
  // but with `loraId === ''` `useLora` is disabled).
  const backend = useLoraWizardBackend(realModeLoraId ?? "");

  // ----- Dataset prep (real mode only; enabled gates the query) -----
  // Track previous loraId to clear its cache when switching passports.
  const prevLoraIdRef = useRef<string | undefined>(undefined);

  // Clear ALL dataset prep caches on mount and when loraId changes.
  // This aggressive approach prevents stale data from a previous passport
  // bleeding through when navigating between passports.
  useLayoutEffect(() => {
    const prevLoraId = prevLoraIdRef.current;

    // If we had a previous loraId, remove its dataset prep cache
    if (prevLoraId && prevLoraId !== realModeLoraId) {
      queryClient.removeQueries({
        queryKey: loraQueryKeys.datasetPrep(prevLoraId),
        exact: true,
      });
    }

    // Also clear the new loraId's cache to ensure fresh fetch
    if (realModeLoraId) {
      queryClient.removeQueries({
        queryKey: loraQueryKeys.datasetPrep(realModeLoraId),
        exact: true,
      });
    }

    prevLoraIdRef.current = realModeLoraId;
  }, [realModeLoraId, queryClient]);

  const prepQuery = useDatasetPrep(realModeLoraId, realMode);

  const startPrep = useStartDatasetPrep();
  const updateCaption = useUpdateDatasetCaption(realModeLoraId ?? "");
  const updateLoraMut = useUpdateLora(realModeLoraId ?? "");
  const setDefaultCheckpoint = useSetDefaultCheckpoint();

  // Guard against stale cache data: only use prep data if its loraId matches
  // the current realModeLoraId. This prevents showing images from a different
  // LoRA when TanStack Query serves cached data during a refetch.
  const prep =
    prepQuery.data?.loraId === realModeLoraId ? prepQuery.data : undefined;

  // Use URLs directly from the API-augmented captions. The API now returns
  // signed downloadUrl and thumbnailUrl for each caption, avoiding the need
  // to rely on useAssets (which filters by status and may exclude pending
  // assets). Prefer thumbnailUrl, fall back to downloadUrl.
  const captionsWithUrls = useMemo(() => {
    if (!prep?.captions) return [];
    return prep.captions.map((c) => ({
      ...c,
      previewUrl: c.thumbnailUrl ?? c.downloadUrl ?? null,
    }));
  }, [prep?.captions]);

  // ----- Real-mode local state -----
  // `selectedModel` and `settings` come from useLoraWizardState in mock
  // mode. In real mode the base model is dictated by `lora.baseModelSlug`,
  // and the settings start at the base model's defaultTrainingConfig.
  const [realModeSettings, setRealModeSettings] =
    useState<TrainingSettings>(DEFAULT_SETTINGS);
  const [realModeSettingsInitialised, setRealModeSettingsInitialised] =
    useState(false);

  useEffect(() => {
    if (!realMode || realModeSettingsInitialised) return;
    const config = backend.selectedBaseModel?.defaultTrainingConfig;
    if (!config) return;
    setRealModeSettings({
      rank:
        typeof config.rank === "number" ? config.rank : DEFAULT_SETTINGS.rank,
      epochs:
        typeof config.epochs === "number"
          ? config.epochs
          : DEFAULT_SETTINGS.epochs,
      learningRate:
        typeof config.learningRate === "number"
          ? config.learningRate
          : DEFAULT_SETTINGS.learningRate,
      snapshotEvery: DEFAULT_SETTINGS.snapshotEvery,
      augmentation: DEFAULT_SETTINGS.augmentation,
    });
    setRealModeSettingsInitialised(true);
  }, [realMode, realModeSettingsInitialised, backend.selectedBaseModel]);

  const [submitError, setSubmitError] = useState<string | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);

  // ----- Real-mode slide auto-advance -----
  // If the user lands on the wizard mid-training, jump them to the
  // Training slide so they can see progress instead of the Intro screen.
  const [autoAdvanced, setAutoAdvanced] = useState(false);
  useEffect(() => {
    if (!realMode || autoAdvanced) return;
    const run = backend.trainingRun;
    if (!run) return;
    if (
      run.statusSlug === "training" ||
      run.statusSlug === "uploading_artifacts" ||
      run.statusSlug === "submitting" ||
      run.statusSlug === "pending_capacity" ||
      run.statusSlug === "queued" ||
      run.statusSlug === "failed_resumable" ||
      run.statusSlug === "resumed"
    ) {
      wizardState.goToSlide(TRAINING_SLIDE);
      setAutoAdvanced(true);
    } else if (run.statusSlug === "completed") {
      wizardState.goToSlide(LOCK_SLIDE);
      setAutoAdvanced(true);
    }
  }, [realMode, autoAdvanced, backend.trainingRun, wizardState]);

  // ----- Auto-select default checkpoint (Feature 2) -----
  // On completion, if the LoRA has no default checkpoint yet, pre-select the
  // highest-step usable one and persist it so the LoRA is immediately usable.
  // The user's explicit pick (below) overrides. Runs at most once.
  const autoDefaultedRef = useRef(false);
  useEffect(() => {
    if (!realMode || autoDefaultedRef.current) return;
    const run = backend.trainingRun;
    if (!run || run.statusSlug !== "completed") return;
    if (backend.lora?.defaultCheckpointId) {
      autoDefaultedRef.current = true;
      return;
    }
    // Usable-for-inference checkpoint statuses, per _lora.lora_checkpoint_statuses
    // ("ready" is not a real status and never matched).
    const usable = (run.checkpoints ?? [])
      .filter((c) =>
        ["available", "evaluating", "evaluated"].includes(c.statusSlug),
      )
      .sort((a, b) => b.stepNumber - a.stepNumber);
    const top = usable[0];
    if (top && realModeLoraId) {
      autoDefaultedRef.current = true;
      setDefaultCheckpoint.mutate({
        loraId: realModeLoraId,
        checkpointId: top.id,
      });
    }
  }, [
    realMode,
    backend.trainingRun,
    backend.lora?.defaultCheckpointId,
    realModeLoraId,
    setDefaultCheckpoint,
  ]);

  // ----- Derived view-model -----
  const steps = realMode ? REAL_MODE_STEPS : MOCK_MODE_STEPS;
  // The slide each mode hides from the indicator + skips in navigation.
  const hiddenSlide = realMode ? TESTING_SLIDE : PREPARE_SLIDE;

  // Map real LoRA's base-model slug to selectedModel for IntroModelSlide.
  // In mock mode we pass through whatever the user picked.
  const selectedModel = realMode
    ? (backend.lora?.baseModelSlug ?? null)
    : wizardState.selectedModel;

  const settings = realMode ? realModeSettings : wizardState.settings;
  const updateSettings = useCallback(
    (next: Partial<TrainingSettings>) => {
      if (realMode) {
        setRealModeSettings((prev) => ({ ...prev, ...next }));
      } else {
        wizardState.updateSettings(next);
      }
    },
    [realMode, wizardState],
  );
  const updateAugmentation = useCallback(
    (next: Partial<TrainingSettings["augmentation"]>) => {
      if (realMode) {
        setRealModeSettings((prev) => ({
          ...prev,
          augmentation: { ...prev.augmentation, ...next },
        }));
      } else {
        wizardState.updateAugmentation(next);
      }
    },
    [realMode, wizardState],
  );

  // In real mode, derive reference list from the dataset project assets.
  // Each Asset gets surfaced as a ReferenceImage with the asset's
  // downloadUrl as the preview.
  const realReferences: ReferenceImage[] = useMemo(() => {
    if (!realMode) return [];
    return backend.datasetAssets
      .filter((a) => (a.mimeType ? a.mimeType.startsWith("image/") : true))
      .map((a) => ({
        id: a.id,
        // ReferenceImage.file is typed as File, but ReferenceCard only
        // reads `previewUrl` — the type is too restrictive for the
        // backend-asset case. We satisfy it with a placeholder; this
        // field is catalogued for Phase 09 widening.
        file: new File([], a.filename ?? a.id),
        previewUrl: a.thumbnailUrl ?? a.downloadUrl ?? "",
        description: a.filename ?? "",
        hasTriggerWord: false,
        needsUpscale: false,
      }));
  }, [realMode, backend.datasetAssets]);

  const references = realMode ? realReferences : wizardState.references;

  // ----- Slide config in real mode -----
  // Real-mode slide configs collapse the testing requirement
  // (`testResults.length === 4`) since automatic test inference is
  // skipped. Otherwise mirrors `SLIDE_CONFIGS`.
  // Prepare step is satisfied once the dataset is prepared. Accept the prep
  // run reaching `prepared`, or the LoRA already advanced past it.
  const isPrepared = useMemo(() => {
    const prepStatus = prep?.prepRun?.statusSlug;
    const loraStatus = backend.lora?.statusSlug;
    return (
      prepStatus === "prepared" ||
      loraStatus === "prepared" ||
      loraStatus === "training" ||
      loraStatus === "ready"
    );
  }, [prep?.prepRun?.statusSlug, backend.lora?.statusSlug]);

  const slideConfigs = useMemo(() => {
    if (!realMode) return SLIDE_CONFIGS;
    return SLIDE_CONFIGS.map((cfg) => {
      if (cfg.id === REFERENCES_SLIDE) {
        return {
          ...cfg,
          canAdvance: () =>
            references.length >= 5 &&
            !backend.isUploadingDatasetAsset &&
            !backend.datasetAssetsLoading,
        };
      }
      if (cfg.id === PREPARE_SLIDE) {
        return { ...cfg, canAdvance: () => isPrepared };
      }
      if (cfg.id === TRAINING_SLIDE) {
        return {
          ...cfg,
          canAdvance: () => {
            const run = backend.trainingRun;
            return Boolean(run && run.statusSlug === "completed");
          },
        };
      }
      if (cfg.id === TESTING_SLIDE) {
        return { ...cfg, canAdvance: () => true, canGoBack: () => false };
      }
      return cfg;
    });
  }, [
    realMode,
    backend.trainingRun,
    references.length,
    isPrepared,
    backend.isUploadingDatasetAsset,
    backend.datasetAssetsLoading,
  ]);

  // ----- Navigation -----
  const currentSlide = wizardState.currentSlide;

  // Determine if training has reached a terminal state (completed/failed/cancelled)
  const trainingHasTerminated = realMode
    ? backend.trainingRun?.statusSlug === "completed" ||
      backend.trainingRun?.statusSlug === "failed" ||
      backend.trainingRun?.statusSlug === "cancelled"
    : !wizardState.isTraining && wizardState.trainingProgress !== null;

  // Lock navigation when:
  // 1. Training is actively running (original behavior), OR
  // 2. Training has terminated AND we're on Training or Lock slide
  //    (prevents going back after training finishes)
  const isLocked = realMode
    ? isTrainingRunActive(backend.trainingRun?.statusSlug) ||
      (trainingHasTerminated && currentSlide >= TRAINING_SLIDE)
    : wizardState.isLocked ||
      (trainingHasTerminated && currentSlide >= TRAINING_SLIDE);

  // Get contextual validation error message (defined before nextSlide which uses it)
  const getValidationMessage = useCallback((): string | null => {
    if (currentSlide === 0 && !selectedModel) {
      return "Please select a base model to continue.";
    }
    if (currentSlide === REFERENCES_SLIDE) {
      // Check uploading/loading states first (real mode only)
      if (realMode && backend.isUploadingDatasetAsset) {
        return "Please wait for uploads to complete.";
      }
      if (realMode && backend.datasetAssetsLoading) {
        return "Please wait for images to load.";
      }
      if (references.length < MIN_PREP_IMAGES) {
        const needed = MIN_PREP_IMAGES - references.length;
        return `Upload at least ${needed} more image${needed === 1 ? "" : "s"} to continue. Minimum ${MIN_PREP_IMAGES} required.`;
      }
    }
    if (currentSlide === PREPARE_SLIDE && !isPrepared) {
      return "Dataset preparation must complete before continuing.";
    }
    return null;
  }, [
    currentSlide,
    selectedModel,
    references.length,
    isPrepared,
    realMode,
    backend.isUploadingDatasetAsset,
    backend.datasetAssetsLoading,
  ]);

  const nextSlide = useCallback(() => {
    const next = currentSlide + 1;
    // Skip the mode-hidden slide (Prepare in mock, Testing in real).
    const target = next === hiddenSlide ? next + 1 : next;
    if (target > LOCK_SLIDE) return;
    const config = slideConfigs[currentSlide];
    if (
      !config ||
      !config.canAdvance({
        ...wizardState,
        references,
        settings,
        isLocked,
        selectedModel,
      } as never)
    ) {
      // Show contextual validation error
      const msg = getValidationMessage();
      if (msg) setValidationError(msg);
      return;
    }
    setValidationError(null);
    wizardState.goToSlide(target);
  }, [
    currentSlide,
    hiddenSlide,
    slideConfigs,
    wizardState,
    references,
    settings,
    isLocked,
    selectedModel,
    getValidationMessage,
  ]);

  const prevSlide = useCallback(() => {
    const prev = currentSlide - 1;
    const target = prev === hiddenSlide ? prev - 1 : prev;
    if (target < 0) return;
    const config = slideConfigs[currentSlide];
    if (!config?.canGoBack({ ...wizardState, isLocked } as never)) return;
    wizardState.goToSlide(target);
  }, [currentSlide, hiddenSlide, slideConfigs, wizardState, isLocked]);

  const goToSlide = useCallback(
    (index: number) => {
      if (index === hiddenSlide) return;
      wizardState.goToSlide(index);
    },
    [hiddenSlide, wizardState],
  );

  // ----- Sync swiper with state -----
  useEffect(() => {
    if (swiperRef.current && swiperRef.current.activeIndex !== currentSlide) {
      swiperRef.current.allowSlideNext = true;
      swiperRef.current.allowSlidePrev = true;
      swiperRef.current.slideTo(currentSlide, 300);
      setTimeout(() => {
        if (swiperRef.current) {
          swiperRef.current.allowSlideNext = !isLocked;
          swiperRef.current.allowSlidePrev = !isLocked;
        }
      }, 350);
    }
  }, [currentSlide, isLocked]);

  // ----- Completion -----
  useEffect(() => {
    if (wizardState.isComplete && onComplete) {
      onComplete();
    }
  }, [wizardState.isComplete, onComplete]);

  // ----- Real-mode action overrides -----
  const handleStartTrainingReal = useCallback(async () => {
    if (!backend.selectedBaseModel) {
      setSubmitError(
        "Base model not loaded. Please wait a moment and try again.",
      );
      return;
    }
    setSubmitError(null);
    try {
      const params = buildSubmitTrainingParams({
        settings: realModeSettings,
        baseModel: backend.selectedBaseModel,
        referenceCount: references.length,
      });
      await backend.submitTraining(
        params as unknown as Record<string, unknown>,
        false, // Set to true for mock training (30s per checkpoint, no AWS Batch)
      );
      wizardState.goToSlide(TRAINING_SLIDE);
    } catch (err) {
      setSubmitError(
        err instanceof Error
          ? err.message
          : "Could not start training. Please try again.",
      );
    }
  }, [backend, realModeSettings, references.length, wizardState]);

  const handleCancelTrainingReal = useCallback(async () => {
    try {
      await backend.cancelTraining();
    } catch {
      // Swallow; the websocket / poller will reflect terminal state.
    }
  }, [backend]);

  // Reset wizard state for a fresh start (keeps assets, resets settings/position)
  const resetWizardForNewTraining = useCallback(() => {
    // Reset local state
    setRealModeSettings(DEFAULT_SETTINGS);
    setRealModeSettingsInitialised(false);
    setAutoAdvanced(false);
    setSubmitError(null);
    autoDefaultedRef.current = false;
    // Reset slide position to beginning
    wizardState.goToSlide(0);
  }, [wizardState]);

  const handleLockReal = useCallback(() => {
    resetWizardForNewTraining();
    if (onComplete) onComplete();
  }, [onComplete, resetWizardForNewTraining]);

  const handleSaveAsDraftReal = useCallback(() => {
    resetWizardForNewTraining();
    if (onComplete) onComplete();
  }, [onComplete, resetWizardForNewTraining]);

  // Mock-mode handlers (existing behaviour)
  const handleStartTrainingMock = wizardState.startTraining;
  const handleCancelTrainingMock = wizardState.cancelTraining;
  const handleLockMock = wizardState.lockLora;
  const handleSaveAsDraftMock = wizardState.saveAsDraft;

  const handleStartTraining = realMode
    ? handleStartTrainingReal
    : handleStartTrainingMock;
  const handleCancelTraining = realMode
    ? handleCancelTrainingReal
    : handleCancelTrainingMock;
  const handleLock = realMode ? handleLockReal : handleLockMock;
  const handleSaveAsDraft = realMode
    ? handleSaveAsDraftReal
    : handleSaveAsDraftMock;

  const handleTrainingContinue = useCallback(() => {
    nextSlide();
  }, [nextSlide]);

  // Trigger-word setter: real-mode commits to backend on blur via the
  // LockSlide; mock mode just updates the local summary.
  const setTriggerWord = useCallback(
    (word: string) => {
      if (realMode) {
        // Fire-and-forget; UI binds the input to the LoRA's
        // current trigger word once useLora refetches.
        void backend.updateTriggerWord(word);
      } else {
        wizardState.setTriggerWord(word);
      }
    },
    [realMode, backend, wizardState],
  );

  // ----- Derived training data for slides -----
  const trainingProgress = realMode
    ? deriveTrainingProgress(backend.trainingRun, realModeSettings)
    : wizardState.trainingProgress;

  const isTraining = realMode
    ? isTrainingRunActive(backend.trainingRun?.statusSlug)
    : wizardState.isTraining;

  const trainingSummary = realMode
    ? deriveTrainingSummary({
        lora: backend.lora,
        trainingRun: backend.trainingRun,
        baseModel: backend.selectedBaseModel,
        settings: realModeSettings,
        castName,
      })
    : wizardState.trainingSummary;

  // ----- Navigation gating -----
  const stateForGate = {
    ...wizardState,
    selectedModel,
    settings,
    references,
    isLocked,
    isTraining,
    trainingProgress,
    trainingSummary,
  };
  const currentConfig = slideConfigs[currentSlide];
  // Underscore prefix: computed but not used - clicking Next shows validation error instead of disabled state
  const _canAdvance = currentConfig?.canAdvance(stateForGate as never) ?? false;
  const canGoBack = currentConfig?.canGoBack(stateForGate as never) ?? false;
  void _canAdvance; // Suppress unused variable warning

  // Clear validation error when slide changes or when user adds references
  useEffect(() => {
    setValidationError(null);
  }, [currentSlide, references.length]);

  const hideNavButtons =
    currentSlide === TRAINING_SLIDE || currentSlide === LOCK_SLIDE;

  const isConfirmSlide = currentSlide === CONFIRM_SLIDE;

  const showNextOnTesting =
    !realMode &&
    currentSlide === TESTING_SLIDE &&
    wizardState.testResults.length === 4 &&
    wizardState.testResults.every((t) => t.accepted);

  // ----- Slot wiring for each slide -----
  const handleAddReferences = useCallback(
    async (files: File[]) => {
      if (realMode) {
        for (const file of files) {
          // Sequential to keep upload progress dialogs readable.
          await backend.uploadDatasetFile(file);
        }
      } else {
        wizardState.addReferences(files);
      }
    },
    [realMode, backend, wizardState],
  );

  const handleRemoveReference = useCallback(
    async (id: string) => {
      if (realMode) {
        const asset = backend.datasetAssets.find((a) => a.id === id);
        if (asset) {
          await backend.removeDatasetAsset(asset);
        }
      } else {
        wizardState.removeReference(id);
      }
    },
    [realMode, backend, wizardState],
  );

  const handleToggleTriggerWord = useCallback(
    (id: string) => {
      // Trigger-word-per-image is a mock-mode-only affordance for v1.
      // Real-mode trigger word lives on the LoRA itself (LockSlide).
      if (!realMode) {
        wizardState.toggleReferenceTriggerWord(id);
      }
    },
    [realMode, wizardState],
  );

  // Handler for adding assets from the library to the reference pool.
  // Fetches the image from the library asset URL and uploads it to the dataset project.
  const handleAddFromLibrary = useCallback(
    async (assets: SelectedAsset[]) => {
      if (!realMode) {
        // Mock mode: library selection not supported
        return;
      }
      for (const asset of assets) {
        try {
          // Fetch the image from the library asset URL
          const response = await fetch(asset.url);
          if (!response.ok) {
            console.error(`Failed to fetch library asset: ${asset.filename}`);
            continue;
          }
          const blob = await response.blob();
          // Create a File object from the blob
          const filename = asset.filename ?? `library-asset-${asset.id}.jpg`;
          const file = new File([blob], filename, { type: blob.type });
          // Upload to the dataset project
          await backend.uploadDatasetFile(file);
        } catch (error) {
          console.error(
            `Failed to add library asset: ${asset.filename}`,
            error,
          );
        }
      }
    },
    [realMode, backend],
  );

  // In real mode, model selection is only allowed when status is "pending"
  const canChangeBaseModel = realMode
    ? backend.lora?.statusSlug === "pending"
    : true;

  const handleSelectModel = useCallback(
    (modelId: string) => {
      if (realMode) {
        // In real mode, only allow changing if status is pending (no training started)
        if (backend.lora?.statusSlug !== "pending") {
          return;
        }
        // Call the API to update the base model
        updateLoraMut.mutate({ baseModelSlug: modelId });
        return;
      }
      wizardState.selectModel(modelId as never);
    },
    [realMode, backend.lora?.statusSlug, wizardState, updateLoraMut],
  );

  return (
    <div className="lora-wizard h-full flex flex-col">
      {/* Header with Timeline and stacked Nav buttons */}
      <div className="wizard-timeline mb-6 flex-shrink-0">
        <div className="flex items-start gap-4">
          <div className="flex-1">
            <div className="flex items-center justify-between">
              {steps.map((step, index) => {
                const isStepComplete = step.id < currentSlide;
                const isCurrent = step.id === currentSlide;
                const isClickable = step.id < currentSlide && !isLocked;

                return (
                  <div
                    key={step.id}
                    className="flex items-center flex-1 last:flex-none"
                  >
                    <button
                      type="button"
                      onClick={() => isClickable && goToSlide(step.id)}
                      disabled={!isClickable}
                      className={cn(
                        "relative flex items-center justify-center w-8 h-8 rounded-full border-2 transition-all",
                        isStepComplete && "bg-[#a654b2] border-[#a654b2]",
                        isCurrent && "bg-primary border-primary",
                        !isStepComplete &&
                          !isCurrent &&
                          "bg-transparent border-white/20",
                        isClickable && "cursor-pointer hover:scale-110",
                        !isClickable && "cursor-default",
                      )}
                    >
                      {isStepComplete ? (
                        <Check className="w-4 h-4 text-white" />
                      ) : (
                        <span
                          className={cn(
                            "text-xs font-medium",
                            isCurrent && "text-white",
                            !isCurrent && "text-white/40",
                          )}
                        >
                          {index + 1}
                        </span>
                      )}
                    </button>

                    {index < steps.length - 1 && (
                      <div
                        className={cn(
                          "flex-1 h-0.5 mx-2 transition-colors",
                          step.id < currentSlide
                            ? "bg-[#a654b2]"
                            : "bg-white/10",
                        )}
                      />
                    )}
                  </div>
                );
              })}
            </div>

            <div className="flex items-center justify-between mt-2">
              {steps.map((step) => {
                const isStepComplete = step.id < currentSlide;
                const isCurrent = step.id === currentSlide;

                return (
                  <div key={step.id} className="flex-1 last:flex-none">
                    <span
                      className={cn(
                        "text-[10px] uppercase tracking-wider block text-center",
                        isCurrent && "text-foreground font-medium",
                        isStepComplete && "text-muted-foreground",
                        !isStepComplete && !isCurrent && "text-white/30",
                      )}
                    >
                      {step.label}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>

          {!hideNavButtons && (
            <div className="flex flex-col gap-1 flex-shrink-0 w-48">
              {(currentSlide < TESTING_SLIDE || showNextOnTesting) && (
                <Button
                  variant={isConfirmSlide ? "ghost" : "pink"}
                  size="sm"
                  onClick={nextSlide}
                  disabled={isConfirmSlide}
                  className="gap-1 w-full justify-center"
                >
                  {currentSlide === TESTING_SLIDE ? "Continue" : "Next"}
                  <ChevronRight className="w-4 h-4" />
                </Button>
              )}
              {currentSlide > 0 && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={prevSlide}
                  disabled={!canGoBack}
                  className="gap-1 w-full justify-center"
                >
                  <ChevronLeft className="w-4 h-4" />
                  Back
                </Button>
              )}
              {validationError && (
                <div
                  className="text-xs text-center px-2 py-1.5 rounded-md mt-1"
                  style={{
                    color: "#e30f63",
                    background: "rgba(227, 15, 99, 0.08)",
                    border: "1px solid rgba(227, 15, 99, 0.2)",
                  }}
                  role="alert"
                >
                  {validationError}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Swiper container - scrollable area */}
      <div className="flex-1 min-h-0 overflow-y-auto">
        <Swiper
          onSwiper={(swiper) => {
            swiperRef.current = swiper;
          }}
          slidesPerView={1}
          allowTouchMove={false}
          allowSlideNext={!isLocked}
          allowSlidePrev={!isLocked}
          autoHeight={true}
          initialSlide={currentSlide}
        >
          <SwiperSlide>
            <div className="slide-content">
              <IntroModelSlide
                selectedModel={selectedModel as never}
                onSelectModel={handleSelectModel as never}
                realModeBaseModels={realMode ? backend.baseModels : undefined}
                realModeLocked={!canChangeBaseModel}
              />
            </div>
          </SwiperSlide>

          <SwiperSlide>
            <div className="slide-content">
              <ReferencesSlide
                references={references}
                onAddReferences={handleAddReferences}
                onRemoveReference={handleRemoveReference}
                onToggleTriggerWord={handleToggleTriggerWord}
                realModeUploading={
                  realMode ? backend.isUploadingDatasetAsset : undefined
                }
                realModeLoading={
                  realMode ? backend.datasetAssetsLoading : undefined
                }
                libraryProjectId={realMode ? libraryProjectId : undefined}
                onAddFromLibrary={realMode ? handleAddFromLibrary : undefined}
              />
            </div>
          </SwiperSlide>

          <SwiperSlide>
            <div className="slide-content">
              {realMode ? (
                <PrepareSlide
                  subjectName={backend.lora?.name ?? castName}
                  onCommitSubjectName={(name) => updateLoraMut.mutate({ name })}
                  isUpdatingSubjectName={updateLoraMut.isPending}
                  triggerWord={
                    prep?.triggerWord ?? backend.lora?.triggerWord ?? null
                  }
                  prepRun={prep?.prepRun ?? null}
                  captions={captionsWithUrls}
                  imageCount={references.length}
                  minImages={MIN_PREP_IMAGES}
                  loading={prepQuery.isLoading}
                  onStartPrep={() => {
                    if (realModeLoraId) startPrep.mutate(realModeLoraId);
                  }}
                  isStarting={startPrep.isPending}
                  onUpdateCaption={(assetId, caption) =>
                    updateCaption.mutate({ assetId, caption })
                  }
                />
              ) : (
                <div className="py-12 text-center text-muted-foreground text-sm">
                  Dataset preparation runs in the real training flow.
                </div>
              )}
            </div>
          </SwiperSlide>

          <SwiperSlide>
            <div className="slide-content">
              <SettingsSlide
                settings={settings}
                onUpdateSettings={updateSettings}
                onUpdateAugmentation={updateAugmentation}
              />
            </div>
          </SwiperSlide>

          <SwiperSlide>
            <div className="slide-content">
              <ConfirmSlide
                selectedModel={selectedModel as never}
                references={references}
                settings={settings}
                onStartTraining={handleStartTraining}
                realModeBaseModel={
                  realMode ? backend.selectedBaseModel : undefined
                }
                realModeSubmitting={
                  realMode ? backend.isSubmittingTraining : undefined
                }
                realModeError={realMode ? submitError : undefined}
                realModeLoraId={realModeLoraId}
              />
            </div>
          </SwiperSlide>

          <SwiperSlide>
            <div className="slide-content">
              <TrainingSlide
                isTraining={isTraining}
                progress={trainingProgress}
                onCancel={handleCancelTraining}
                onContinue={handleTrainingContinue}
                realModeStatusSlug={
                  realMode ? backend.trainingRun?.statusSlug : undefined
                }
                realModeErrorMessage={
                  realMode
                    ? (backend.trainingRun?.errorMessage ?? null)
                    : undefined
                }
                realModeCancelling={
                  realMode ? backend.isCancellingTraining : undefined
                }
                realModeCheckpoints={
                  realMode ? backend.trainingRun?.checkpoints : undefined
                }
                realModeDefaultCheckpointId={
                  realMode ? backend.lora?.defaultCheckpointId : undefined
                }
                onSelectCheckpoint={
                  realMode && realModeLoraId
                    ? (checkpointId) =>
                        setDefaultCheckpoint.mutate({
                          loraId: realModeLoraId,
                          checkpointId,
                        })
                    : undefined
                }
                realModeSettingDefault={
                  realMode ? setDefaultCheckpoint.isPending : undefined
                }
                realModeResumeAttemptCount={
                  realMode ? backend.trainingRun?.resumeAttemptCount : undefined
                }
                realModeResumedFromRunId={
                  realMode ? backend.trainingRun?.resumedFromRunId : undefined
                }
                realModeResumeFromCheckpointStep={
                  realMode
                    ? backend.trainingRun?.resumeFromCheckpointStep
                    : undefined
                }
                onRetry={realMode ? handleStartTrainingReal : undefined}
                realModeRetrying={
                  realMode ? backend.isSubmittingTraining : undefined
                }
                realModeRetryError={realMode ? submitError : undefined}
              />
            </div>
          </SwiperSlide>

          <SwiperSlide>
            <div className="slide-content">
              {realMode ? (
                // Testing slide is skipped in real mode (catalogued for
                // Phase 09). Render a thin placeholder so the swiper's
                // slide count matches SLIDE_CONFIGS.
                <div className="py-12 text-center text-muted-foreground text-sm">
                  Testing is skipped in this flow.
                </div>
              ) : (
                <TestingSlide
                  testResults={wizardState.testResults}
                  onRegenerate={wizardState.regenerateTest}
                  onAccept={wizardState.acceptTest}
                />
              )}
            </div>
          </SwiperSlide>

          <SwiperSlide>
            <div className="slide-content">
              <LockSlide
                summary={trainingSummary}
                onSetTriggerWord={setTriggerWord}
                onLock={handleLock}
                onSaveAsDraft={handleSaveAsDraft}
                realModeUpdatingTrigger={
                  realMode ? backend.isUpdatingTriggerWord : undefined
                }
                realModeLora={realMode ? backend.lora : undefined}
                realModeCheckpoints={
                  realMode ? backend.trainingRun?.checkpoints : undefined
                }
                realModeDefaultCheckpointId={
                  realMode ? backend.lora?.defaultCheckpointId : undefined
                }
                onSelectCheckpoint={
                  realMode && realModeLoraId
                    ? (checkpointId) =>
                        setDefaultCheckpoint.mutate({
                          loraId: realModeLoraId,
                          checkpointId,
                        })
                    : undefined
                }
                realModeSettingDefault={
                  realMode ? setDefaultCheckpoint.isPending : undefined
                }
              />
            </div>
          </SwiperSlide>
        </Swiper>
      </div>
    </div>
  );
}
